import {
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { styled } from 'styled-components'

import Checkbox from 'core/components/lib/Checkbox'
import { EmptyModelState } from 'core/components/lib/EmptyState'
import Skeleton from 'core/components/lib/Skeleton'
import { radiusCard } from 'core/styles'
import {
  onSurface,
  onSurfaceVariant,
  outline,
  surfaceBrightHover,
  surfaceDim,
} from 'core/styles/tokens'

import { isNoWrapColumn } from './noWrapColumns'

// A title only breaks when breaking it is the lesser evil. Below this, the
// column widens to fit; above it, wrapping beats a very wide column.
const HEADER_WRAP_THRESHOLD = 35

const isShortHeader = (header: unknown): boolean =>
  typeof header === 'string' && header.length <= HEADER_WRAP_THRESHOLD
import { TableColumnDef, TableColumnMeta } from './types'

type TableCommonProps<Row> = {
  className?: string
  columns: Array<TableColumnDef<Row>>
  data: Array<Row>
  isLoading?: boolean
  emptyHeading?: string
  emptySubheading?: string
  /** Receives the plain row object (row.original), not TanStack's Row wrapper. */
  onRowClick?: (row: Row) => void
}

type TableSelectionProps<Row> =
  | {
      enableRowSelection: true
      /** Required when enableRowSelection is true: selection needs stable row identity. */
      getRowId: (row: Row) => string
      /** Keyed by the id returned from getRowId. Table never owns this state itself. */
      rowSelection: { [rowId: string]: boolean }
      onRowSelectionChange: (next: { [rowId: string]: boolean }) => void
    }
  | {
      enableRowSelection?: false
      /** Stable row identity (rows are API objects with ids); defaults to array index. */
      getRowId?: (row: Row) => string
      rowSelection?: never
      onRowSelectionChange?: never
    }

type TableProps<Row> = TableCommonProps<Row> & TableSelectionProps<Row>

const SELECTION_COLUMN_ID = '__select__'

const SKELETON_ROWS = 3

const Table = <Row,>({
  className,
  columns,
  data,
  getRowId,
  isLoading = false,
  emptyHeading,
  emptySubheading,
  onRowClick,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
}: TableProps<Row>) => {
  const selectionColumn: TableColumnDef<Row> = {
    id: SELECTION_COLUMN_ID,
    meta: { width: '40px' },
    header: ({ table }) => (
      <SelectionCell onClick={(e) => e.stopPropagation()}>
        <Checkbox
          ariaLabel='Select all rows'
          onChange={() => table.toggleAllRowsSelected()}
          value={
            table.getIsAllRowsSelected()
              ? true
              : table.getIsSomeRowsSelected()
              ? 'indeterminate'
              : false
          }
        />
      </SelectionCell>
    ),
    cell: ({ row }) => (
      <SelectionCell
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Checkbox
          ariaLabel={`Select row ${row.id}`}
          onChange={() => row.toggleSelected()}
          value={row.getIsSelected()}
        />
      </SelectionCell>
    ),
  }

  const tableColumns = enableRowSelection
    ? [selectionColumn, ...columns]
    : columns

  const table = useReactTable({
    columns: tableColumns as Array<ColumnDef<Row, unknown>>,
    data,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: (updater) => {
      if (!onRowSelectionChange) return
      const current = rowSelection ?? {}
      const next = typeof updater === 'function' ? updater(current) : updater
      onRowSelectionChange(next)
    },
    state: { rowSelection: rowSelection ?? {} },
    ...(getRowId && { getRowId: (row: Row) => getRowId(row) }),
  })

  const primaryColumnIndex = enableRowSelection ? 1 : 0

  const onRowKeyDown =
    (row: Row) => (e: KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onRowClick?.(row)
      }
    }

  const viewportRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollShadows = () => {
    const viewport = viewportRef.current
    if (!viewport) return

    setCanScrollLeft(viewport.scrollLeft > 0)
    setCanScrollRight(
      viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - 1,
    )
  }

  useLayoutEffect(() => {
    updateScrollShadows()
  })

  useEffect(() => {
    window.addEventListener('resize', updateScrollShadows)
    return () => window.removeEventListener('resize', updateScrollShadows)
  }, [])

  return (
    <TableWrapper className={className}>
      <TableViewport onScroll={updateScrollShadows} ref={viewportRef}>
        <StyledTable>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as
                    | TableColumnMeta
                    | undefined

                  return (
                    <HeaderCell
                      $align={meta?.align}
                      $noWrap={
                        meta?.noWrap ??
                        (isShortHeader(header.column.columnDef.header) ||
                          isNoWrapColumn(header.column.id))
                      }
                      $width={meta?.width}
                      key={header.id}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </HeaderCell>
                  )
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {isLoading ? (
              Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                <tr key={rowIndex}>
                  {table.getAllColumns().map((column) => (
                    <Cell key={column.id}>
                      {column.id !== SELECTION_COLUMN_ID && (
                        <Skeleton height='16px' width='80%'>
                          <rect height='16' rx='4' ry='4' width='100%' />
                        </Skeleton>
                      )}
                    </Cell>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <EmptyCell colSpan={tableColumns.length}>
                  <EmptyModelState
                    heading={emptyHeading}
                    subheading={emptySubheading}
                  />
                </EmptyCell>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <BodyRow
                  $clickable={!!onRowClick}
                  key={row.id}
                  onClick={onRowClick && (() => onRowClick(row.original))}
                  onKeyDown={onRowClick && onRowKeyDown(row.original)}
                  tabIndex={onRowClick ? 0 : undefined}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const meta = cell.column.columnDef.meta as
                      | TableColumnMeta
                      | undefined

                    return (
                      <Cell
                        $align={meta?.align}
                        $muted={cellIndex !== primaryColumnIndex}
                        $noWrap={meta?.noWrap ?? true}
                        key={cell.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Cell>
                    )
                  })}
                </BodyRow>
              ))
            )}
          </tbody>
        </StyledTable>
      </TableViewport>
      {canScrollLeft && <ScrollShadow $side='left' data-scroll-shadow='left' />}
      {canScrollRight && (
        <ScrollShadow $side='right' data-scroll-shadow='right' />
      )}
    </TableWrapper>
  )
}

export default Table

const TableWrapper = styled.div`
  position: relative;
  border: 1px solid ${(p) => p.theme.border ?? outline};
  border-radius: ${radiusCard};
  overflow: hidden;
`

const TableViewport = styled.div`
  width: 100%;
  overflow-x: auto;
`

const ScrollShadow = styled.div<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${(p) => (p.$side === 'left' ? 'left: 0;' : 'right: 0;')}
  background: linear-gradient(
    to ${(p) => (p.$side === 'left' ? 'right' : 'left')},
    ${(p) => p.theme.hover ?? surfaceBrightHover},
    transparent
  );
  width: 24px;
  pointer-events: none;
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const HeaderCell = styled.th<{
  $align?: 'left' | 'right'
  $noWrap?: boolean
  $width?: string
}>`
  border-bottom: 1px solid ${(p) => p.theme.border ?? outline};
  background: ${(p) => p.theme.surfaceDim ?? surfaceDim};
  padding: 0 8px;
  width: ${(p) => p.$width ?? 'auto'};
  height: 40px;
  text-align: ${(p) => p.$align ?? 'left'};
  white-space: ${(p) => (p.$noWrap ? 'nowrap' : 'normal')};
  color: ${(p) => p.theme.text ?? onSurface};
  font-size: 14px;
  font-weight: 600;
`

const Cell = styled.td<{
  $align?: 'left' | 'right'
  $muted?: boolean
  $noWrap?: boolean
}>`
  border-bottom: 1px solid ${(p) => p.theme.border ?? outline};
  padding: 0 8px;
  height: 40px;
  text-align: ${(p) => p.$align ?? 'left'};
  white-space: ${(p) => (p.$noWrap ? 'nowrap' : 'normal')};
  color: ${(p) =>
    p.$muted
      ? p.theme.labelText ?? onSurfaceVariant
      : p.theme.text ?? onSurface};
  font-size: 14px;
`

const EmptyCell = styled.td`
  padding: 8px 0;
`

const BodyRow = styled.tr<{ $clickable: boolean }>`
  ${(p) =>
    p.$clickable &&
    `
    cursor: pointer;

    &:hover,
    &:focus-visible {
      background: ${p.theme.hover ?? surfaceBrightHover};
    }
  `}
`

const SelectionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`
